/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {
  AxiosError,
  type AxiosInstance,
  AxiosInterceptorManager,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { BUILD_TARGET, HOST_URL } from "config";
import { appLog } from "utils/webview";
import * as Sentry from "@sentry/react";

export type CustomResponseFormat<T = any> = {
  code: number;
  debugMessage?: string;
  message?: string;
  status?: number;
  error?: string;
  data?: T;
  result?: T;
};

interface CustomInstance extends AxiosInstance {
  interceptors: {
    request: AxiosInterceptorManager<InternalAxiosRequestConfig>;
    response: AxiosInterceptorManager<AxiosResponse<CustomResponseFormat>>;
  };

  getUri(config?: AxiosRequestConfig): string;

  request<T>(config: AxiosRequestConfig): Promise<T>;

  get<T>(url: string, config?: AxiosRequestConfig): Promise<T>;

  delete<T>(url: string, config?: AxiosRequestConfig): Promise<T>;

  head<T>(url: string, config?: AxiosRequestConfig): Promise<T>;

  options<T>(url: string, config?: AxiosRequestConfig): Promise<T>;

  post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;

  put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;

  patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
}

const onRequest = (
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
  if (BUILD_TARGET === "dev") {
    console.info(
      "[",
      config.method?.toUpperCase(),
      config.url,
      "]",
      "\nparams: ",
      config.params
    );
  }
  return config;
};

const onResponse = (response: AxiosResponse): AxiosResponse => {
  if (BUILD_TARGET === "dev") {
    // console.info(
    //   `[${response.config.method?.toUpperCase()} ${
    //     response.config.url
    //   }]\ndata: ${JSON.stringify(response.data)}`
    // );
    // console.log(response.data);
  }
  return response.data;
};

const axiosInstance: CustomInstance = axios.create({
  withCredentials: true,
  baseURL: HOST_URL,
  timeout: 15000, // 15초 타임아웃
  headers: {
    // "X-Api-Key": API_KEY,
    // "X-Auth-Token": "",
    // "X-App-Id": "w:123",
  },
});

const onFulfilled = (response: AxiosResponse) => response;

const onRejected = (error: AxiosError<CustomResponseFormat>) => {
  if (error.config) console.log(error);

  // 네트워크 에러 또는 타임아웃 처리
  if (!error.response) {
    if (error.code === 'ECONNABORTED') {
      console.error('Request timeout:', error.message);
      Sentry.captureException(error, {
        tags: { action: "axios_timeout" },
      });
    } else if (error.message === 'Network Error') {
      console.error('Network error:', error.message);
      Sentry.captureException(error, {
        tags: { action: "axios_network_error" },
      });
    }
  }

  if (error.response?.data) {
    const statusCode = error.response?.data.status;

    if (statusCode === 401 || statusCode === 403) {
      console.log(`401 ${JSON.stringify(error.config)}`);
      appLog(`401 ${JSON.stringify(error.config)}`);
      localStorage.removeItem("user");
      window.location.href = `${window.location.origin}/login`;
    }
  }

  return Promise.reject(error);
};

axiosInstance.interceptors.request.use(onRequest);
// axiosInstance.interceptors.response.use(onResponse);
axiosInstance.interceptors.response.use(onResponse, onRejected);

axiosInstance.defaults.headers["X-Auth-Token"] = localStorage.getItem("user")
  ? JSON.parse(localStorage.getItem("user") || "").accessToken
  : undefined;

export const setAuthHeaders = (accessToken: string, appId?: string) => {
  if (
    axiosInstance.defaults?.headers &&
    axiosInstance.defaults.headers.common
  ) {
    // console.log("current", accessToken, appId);
    // @ts-ignore
    axiosInstance.defaults.headers["X-Auth-Token"] = accessToken;
    if (appId) {
      // @ts-ignore
      axiosInstance.defaults.headers["X-App-Id"] = appId;
    }
  }
};

export const setAppIdHeaders = (appId: string) => {
  if (
    axiosInstance.defaults?.headers &&
    axiosInstance.defaults.headers.common
  ) {
    axiosInstance.defaults.headers["X-App-Id"] = appId;
  }
};

export const setLocale = (locale: string) => {
  if (
    axiosInstance.defaults?.headers &&
    axiosInstance.defaults.headers.common
  ) {
    // console.log("current", accessToken, appId);
    // @ts-ignore
    axiosInstance.defaults.headers["X-Lang-Code"] = locale;
  }
};

export { axiosInstance };
